
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowRight, User, Mail, Lock, UserCircle, LineChart } from 'lucide-react';
import AuthLayout from '../../../layouts/AuthLayout';
import FormInput from '../../../components/forms/FormInput';
import SubmitButton from '../../../components/buttons/SubmitButton';
import Alert from '../../../components/feedback/Alert';
import { APP_NAME } from '../../../constants/appConstants';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password2: '',
    first_name: '',
    last_name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const { register } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if ((name === 'password' || name === 'password2') && error === "Passwords don't match") {
        setError('');
    }
    // Clear username validation errors when user types
    if (name === 'username' && error.includes('Username')) {
        setError('');
    }
  };

  const validateUsername = (username) => {
    if (username.includes('@')) {
      return "Username cannot contain '@' symbol";
    }
    const invalidChars = ['@', '<', '>', '"', "'", '&', '%', '$', '#'];
    for (const char of invalidChars) {
      if (username.includes(char)) {
        return `Username cannot contain '${char}' character`;
      }
    }
    // Check for email-like format
    if (username.includes('.') && username.split('.').length > 1) {
      const parts = username.split('.');
      if (parts[parts.length - 1].length >= 2) {
        return "Username cannot be in email format";
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate username
    const usernameError = validateUsername(formData.username);
    if (usernameError) {
      return setError(usernameError);
    }
    
    if (formData.password !== formData.password2) {
      return setError("Passwords don't match");
    }
    
    setLoading(true);
    
    try {
      await register(formData);
    } catch (err) {
      let errorMsg = 'Registration failed';
      
      try {
        const errorData = JSON.parse(err.message);
        errorMsg = Object.entries(errorData)
          .map(([key, value]) => `${key}: ${value.join(' ')}`)
          .join('\n');
      } catch {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const leftPanel = (
    <>
      <h2 className="text-xl font-bold mb-2">Join {APP_NAME}</h2>
      <p className="mb-6 text-blue-100 text-xs">Create an account and start managing your finances.</p>
      <div className="space-y-5">
        <div className="flex items-start">
          <div className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center mr-3 flex-shrink-0">
            <LineChart className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-0.5">Financial Reports</h3>
            <p className="text-blue-100 text-xs">Manage detailed reports</p>
          </div>
        </div>
        <div className="flex items-start">
          <div className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center mr-3 flex-shrink-0">
            <User className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-0.5">Team Collaboration</h3>
            <p className="text-blue-100 text-xs">Invite team members</p>
          </div>
        </div>
        <div className="flex items-start">
          <div className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center mr-3 flex-shrink-0">
            <Lock className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-0.5">Secure Access</h3>
            <p className="text-blue-100 text-xs">Enterprise encryption</p>
          </div>
        </div>
      </div>
    </>
  );

  const passwordMismatch = formData.password2 && formData.password !== formData.password2;

  return (
    <AuthLayout leftPanelContent={leftPanel} isLoginPage={false}>
      <div className="mb-4">
        <p className="text-sm text-gray-600">Fill in your details to get started</p>
      </div>

      <Alert message={error === "Passwords don't match" ? null : error} type="error" /> 
 
      <form onSubmit={handleSubmit} className="space-y-3 mt-5">
        <div className="display flex items-center gap-4">
          {/* Left Column */}
          <div className="space-y-3 w-[50%]">
            <FormInput
              id="username"
              name="username"
              label="Username"
              type="text"
              required
              icon={User}
              placeholder="Choose a username"
              value={formData.username}
              onChange={handleChange}
              autoComplete="username"
            >
              <p className="mt-1 text-xs text-gray-500">
                Username cannot contain @ or special characters
              </p>
            </FormInput>

            <FormInput
              id="email"
              name="email"
              label="Email"
              type="email"
              required
              icon={Mail}
              placeholder="Your email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
            />

            <FormInput
              id="password"
              name="password"
              label="Password"
              type="password"
              required
              icon={Lock}
              placeholder="Create password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
            >
              <div className="mt-1.5">
                <div className="w-full bg-gray-200 rounded-full h-1">
                  <div className={`h-1 rounded-full transition-all duration-300 ${
                    formData.password.length === 0 ? 'w-0 bg-gray-200' :
                    formData.password.length < 6 ? 'w-1/4 bg-red-500' :
                    formData.password.length < 10 ? 'w-2/4 bg-yellow-500' :
                    'w-full bg-green-500'
                  }`}></div>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formData.password.length === 0 ? 'Password strength' :
                  formData.password.length < 6 ? 'Weak' :
                  formData.password.length < 10 ? 'Medium' :
                  'Strong'}
                </p>
              </div>
            </FormInput>
          </div>

          {/* Right Column */}
          <div className="space-y-3 w-[50%]">

            <FormInput
              id="first_name"
              name="first_name"
              label="First Name"
              type="text"
              icon={UserCircle}
              placeholder="First name"
              value={formData.first_name}
              onChange={handleChange}
              autoComplete="given-name"
            />

            <FormInput
              id="last_name"
              name="last_name"
              label="Last Name"
              type="text"
              icon={UserCircle}
              placeholder="Last name"
              value={formData.last_name}
              onChange={handleChange}
              autoComplete="family-name"
            />



            

            <FormInput
              id="password2"
              name="password2"
              label="Confirm Password"
              type="password"
              required
              icon={Lock}
              placeholder="Confirm password"
              value={formData.password2}
              onChange={handleChange}
              autoComplete="new-password"
              error={passwordMismatch}
            >
               {passwordMismatch && (
                 <p className="mt-1 text-xs text-red-500">Passwords don't match</p>
               )}
            </FormInput>
          </div>
        </div>

        <div className="flex items-start pt-1">
          <input
            id="terms"
            name="terms"
            type="checkbox"
            required
            checked={agreeToTerms}
            onChange={() => setAgreeToTerms(!agreeToTerms)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
          />
          <label htmlFor="terms" className="ml-2 block text-xs text-gray-700">
            I agree to the <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-medium">Terms</Link> and <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">Privacy Policy</Link>
          </label>
        </div>

        <div className="flex justify-end pt-2">
          <SubmitButton 
            isLoading={loading} 
            disabled={passwordMismatch || !agreeToTerms} 
            icon={ArrowRight}
            fullWidth={false}
            className="px-8"
          >
            Create Account
          </SubmitButton>
        </div>
      </form>

      <div className="mt-4 flex items-start pt-1">
        <p className="text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;